import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';

// --- STYLES COMPONENT ---
// Injects all the custom CSS into the document head
const PortfolioStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
    
    html {
        scroll-behavior: smooth;
    }

    body {
        font-family: 'Inter', sans-serif;
        background-color: #0a0a0a; /* Near black */
        color: #e0e0e0; /* Off-white text */
        overflow-x: hidden; /* Prevent horizontal scroll */
    }

    /* --- Custom Scrollbar --- */
    ::-webkit-scrollbar {
        width: 8px;
    }
    ::-webkit-scrollbar-track {
        background: #1a1a1a;
    }
    ::-webkit-scrollbar-thumb {
        background: #4f46e5; /* Indigo */
        border-radius: 4px;
    }
    ::-webkit-scrollbar-thumb:hover {
        background: #6366f1;
    }

    /* --- Three.js Canvas --- */
    #bg-canvas {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 0;
        opacity: 0.6;
    }

    /* --- Navigation --- */
    .nav-link {
        position: relative;
        transition: color 0.3s ease;
    }
    .nav-link::after {
        content: '';
        position: absolute;
        width: 0;
        height: 2px;
        bottom: -4px;
        left: 50%;
        transform: translateX(-50%);
        background-color: #6366f1; /* Indigo */
        transition: width 0.3s ease;
    }
    .nav-link:hover::after,
    .nav-link.active::after {
        width: 100%;
    }
    .nav-link.active {
        color: #818cf8; /* Lighter Indigo */
    }
    
    /* --- Hero Button Glow --- */
    .cta-glow {
        box-shadow: 0 0 15px rgba(99, 102, 241, 0.5), 0 0 30px rgba(99, 102, 241, 0.3);
        animation: pulse-glow 2.5s infinite ease-in-out;
    }
    @keyframes pulse-glow {
        0% { box-shadow: 0 0 15px rgba(99, 102, 241, 0.5), 0 0 30px rgba(99, 102, 241, 0.3); }
        50% { box-shadow: 0 0 25px rgba(129, 140, 248, 0.7), 0 0 40px rgba(129, 140, 248, 0.5); }
        100% { box-shadow: 0 0 15px rgba(99, 102, 241, 0.5), 0 0 30px rgba(99, 102, 241, 0.3); }
    }

    /* --- Scroll Reveal Animation --- */
    .reveal {
        opacity: 0;
        transform: translateY(30px);
        transition: opacity 0.8s ease-out, transform 0.8s ease-out;
    }
    .reveal.visible {
        opacity: 1;
        transform: translateY(0);
    }

    /* --- Vertical Timeline for Experience --- */
    .timeline-container {
        position: relative;
        max-width: 1000px;
        margin: 0 auto;
    }
    .timeline-container::after {
        content: '';
        position: absolute;
        width: 3px;
        background-color: #374151; /* Gray 700 */
        top: 0;
        bottom: 0;
        left: 50%;
        margin-left: -1.5px;
    }
    .timeline-item {
        padding: 10px 40px;
        position: relative;
        width: 50%;
    }
    .timeline-item::after {
        content: '';
        position: absolute;
        width: 20px;
        height: 20px;
        right: -10px;
        background-color: #0a0a0a;
        border: 4px solid #4f46e5; /* Indigo */
        top: 28px;
        border-radius: 50%;
        z-index: 1;
    }
    /* Item on the left */
    .timeline-left {
        left: 0;
    }
    /* Item on the right */
    .timeline-right {
        left: 50%;
    }
    .timeline-right::after {
        left: -10px;
    }
    .timeline-content {
        padding: 20px 30px;
        background-color: #1f2937; /* Gray 800 */
        position: relative;
        border-radius: 8px;
        border: 1px solid #374151;
        transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
    }
    .timeline-content:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 20px rgba(79, 70, 229, 0.1);
        border-color: #4f46e5;
    }

    /* Responsive Timeline */
    @media screen and (max-width: 768px) {
        .timeline-container::after {
            left: 31px;
        }
        .timeline-item {
            width: 100%;
            padding-left: 70px;
            padding-right: 25px;
        }
        .timeline-left {
            left: 0;
        }
        .timeline-right {
            left: 0;
        }
        .timeline-right::after {
            left: 21px; /* Adjusted for left alignment */
        }
        .timeline-left::after {
            left: 21px;
        }
    }

    /* --- Skill Pill --- */
    .skill-pill {
        background-color: #1f2937; /* Gray 800 */
        border: 1px solid #374151; /* Gray 700 */
        transition: all 0.3s ease;
    }
    .skill-pill:hover {
        background-color: #374151;
        transform: scale(1.05);
        color: #c7d2fe; /* Indigo 200 */
    }

    /* --- Interactive Card (Projects, ECs) --- */
    .interactive-card {
        border: 1px solid #374151; /* Gray 700 */
        transition: all 0.3s ease;
    }
    .interactive-card:hover {
        transform: translateY(-6px);
        box-shadow: 0 10px 25px rgba(79, 70, 229, 0.2), 0 0 15px rgba(79, 70, 229, 0.1);
        border-color: #4f46e5; /* Indigo 600 */
    }
  `}</style>
);

// --- Custom Hook for Scroll Reveal ---
const useScrollReveal = () => {
    useEffect(() => {
        const revealElements = document.querySelectorAll('.reveal');
        const revealObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                    revealObserver.unobserve(entry.target); // Only animate once
                }
            });
        }, { threshold: 0.1 }); // Trigger when 10% is visible

        revealElements.forEach(el => {
            revealObserver.observe(el);
        });

        // Cleanup
        return () => revealElements.forEach(el => revealObserver.unobserve(el));
    }, []);
};

// --- Custom Hook for Active Nav Link ---
const useActiveNav = (setActiveSection) => {
    useEffect(() => {
        const sections = document.querySelectorAll('section[id]');
        
        const navObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                    setActiveSection(entry.target.getAttribute('id'));
                }
            });
        }, { threshold: 0.5 }); // Trigger when 50% of section is visible

        sections.forEach(section => {
            navObserver.observe(section);
        });

        // Cleanup
        return () => sections.forEach(section => navObserver.unobserve(section));
    }, [setActiveSection]);
};

// --- Three.js Background Component ---
const ThreeJSBackground = () => {
    const canvasRef = useRef(null);
    
    useEffect(() => {
        let scene, camera, renderer, particles, lines;
        let mouse = new THREE.Vector2(-100, -100); // Start off-screen
        const particleCount = 500;
        const connectionDistance = 100;
        let animationFrameId;

        function initThreeJS() {
            try {
                const container = canvasRef.current;
                if (!container) return;

                // Scene
                scene = new THREE.Scene();

                // Camera
                camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
                camera.position.z = 300;

                // Renderer
                renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
                renderer.setSize(window.innerWidth, window.innerHeight);
                renderer.setClearColor(0x000000, 0); // Transparent background
                container.appendChild(renderer.domElement);

                // Particles
                const particleGeometry = new THREE.BufferGeometry();
                const positions = new Float32Array(particleCount * 3);
                const velocities = new Float32Array(particleCount * 3);

                for (let i = 0; i < particleCount * 3; i += 3) {
                    positions[i] = (Math.random() - 0.5) * 800; // x
                    positions[i + 1] = (Math.random() - 0.5) * 800; // y
                    positions[i + 2] = (Math.random() - 0.5) * 800; // z

                    velocities[i] = (Math.random() - 0.5) * 0.5;
                    velocities[i + 1] = (Math.random() - 0.5) * 0.5;
                    velocities[i + 2] = (Math.random() - 0.5) * 0.5;
                }
                particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
                particleGeometry.setAttribute('velocity', new THREE.BufferAttribute(velocities, 3));

                const particleMaterial = new THREE.PointsMaterial({
                    color: 0x818cf8, // Indigo 300
                    size: 2,
                    blending: THREE.AdditiveBlending,
                    transparent: true,
                    sizeAttenuation: true
                });
                particles = new THREE.Points(particleGeometry, particleMaterial);
                scene.add(particles);

                // Lines
                const lineGeometry = new THREE.BufferGeometry();
                const linePositions = new Float32Array(particleCount * particleCount * 3); // Max possible
                lineGeometry.setAttribute('position', new THREE.BufferAttribute(linePositions, 3));
                const lineMaterial = new THREE.LineBasicMaterial({
                    color: 0x4f46e5, // Indigo 600
                    transparent: true,
                    opacity: 0.1,
                    blending: THREE.AdditiveBlending,
                });
                lines = new THREE.LineSegments(lineGeometry, lineMaterial);
                scene.add(lines);

                // Event Listeners
                window.addEventListener('resize', onWindowResize);
                document.addEventListener('mousemove', onMouseMove);

                animateThreeJS();
            } catch(e) {
                console.error("Failed to initialize three.js:", e);
                // Hide canvas if it fails
                if(canvasRef.current) canvasRef.current.style.display = 'none';
            }
        }

        function onWindowResize() {
            if (!camera || !renderer) return;
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
        }

        function onMouseMove(event) {
            // Normalized device coordinates
            mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
            mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
        }

        function animateThreeJS() {
            animationFrameId = requestAnimationFrame(animateThreeJS);
            
            if (!particles || !lines || !renderer || !scene || !camera) return;

            const positions = particles.geometry.attributes.position.array;
            const velocities = particles.geometry.attributes.velocity.array;
            
            const linePositions = lines.geometry.attributes.position.array;
            let lineIndex = 0;

            // Create a mouse vector in 3D space
            const mouseWorld = new THREE.Vector3(mouse.x, mouse.y, 0.5);
            mouseWorld.unproject(camera);
            mouseWorld.sub(camera.position).normalize();
            const distance = -camera.position.z / mouseWorld.z;
            const mousePos = new THREE.Vector3().copy(camera.position).add(mouseWorld.multiplyScalar(distance));


            for (let i = 0; i < particleCount; i++) {
                const i3 = i * 3;
                
                // Update position
                positions[i3] += velocities[i3];
                positions[i3 + 1] += velocities[i3 + 1];
                positions[i3 + 2] += velocities[i3 + 2];

                // Check boundaries
                if (positions[i3] > 400 || positions[i3] < -400) velocities[i3] *= -1;
                if (positions[i3 + 1] > 400 || positions[i3 + 1] < -400) velocities[i3 + 1] *= -1;
                if (positions[i3 + 2] > 400 || positions[i3 + 2] < -400) velocities[i3 + 2] *= -1;

                // Mouse interaction (repel)
                const dx = positions[i3] - mousePos.x;
                const dy = positions[i3 + 1] - mousePos.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                
                if (dist < 100) { // 100px radius
                    const force = (100 - dist) / 100 * 0.5; // Repel force
                    velocities[i3] += (dx / dist) * force;
                    velocities[i3 + 1] += (dy / dist) * force;
                }

                // Check connections
                for (let j = i + 1; j < particleCount; j++) {
                    const j3 = j * 3;
                    const dx = positions[i3] - positions[j3];
                    const dy = positions[i3 + 1] - positions[j3 + 1];
                    const dz = positions[i3 + 2] - positions[j3 + 2];
                    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

                    if (dist < connectionDistance) {
                        linePositions[lineIndex++] = positions[i3];
                        linePositions[lineIndex++] = positions[i3 + 1];
                        linePositions[lineIndex++] = positions[i3 + 2];
                        linePositions[lineIndex++] = positions[j3];
                        linePositions[lineIndex++] = positions[j3 + 1];
                        linePositions[lineIndex++] = positions[j3 + 2];
                    }
                }
            }
            
            // Update geometries
            particles.geometry.attributes.position.needsUpdate = true;
            lines.geometry.attributes.position.needsUpdate = true;
            lines.geometry.setDrawRange(0, lineIndex / 3); // Only draw valid lines

            // Rotate the whole scene
            scene.rotation.y += 0.0002;
            scene.rotation.x += 0.0001;

            renderer.render(scene, camera);
        }

        initThreeJS();

        // Cleanup function
        return () => {
            cancelAnimationFrame(animationFrameId);
            window.removeEventListener('resize', onWindowResize);
            document.removeEventListener('mousemove', onMouseMove);
            if (renderer) {
                renderer.dispose();
                const currentContainer = canvasRef.current;
                if (renderer.domElement && currentContainer && currentContainer.contains(renderer.domElement)) {
                    currentContainer.removeChild(renderer.domElement);
                }
            }
            // You can also dispose geometries and materials here if needed
        };
    }, []); // Empty array ensures this runs once on mount

    return <div id="bg-canvas" ref={canvasRef}></div>;
};


// --- HEADER COMPONENT ---
const Header = ({ activeSection }) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const navItems = [
        { href: "#home", label: "Home", section: "home" },
        { href: "#about", label: "About", section: "about" },
        { href: "#value-prop", label: "Value", section: "value-prop" },
        { href: "#skills", label: "Skills", section: "skills" },
        { href: "#projects", label: "Projects", section: "projects" },
        { href: "#experience", label: "Experience", section: "experience" },
        { href: "#extracurriculars", label: "Extracurriculars", section: "extracurriculars" },
        { href: "#contact", label: "Contact", section: "contact" },
    ];

    const handleScrollTo = (e) => {
        e.preventDefault();
        const targetId = e.currentTarget.getAttribute('href');
        const targetElement = document.querySelector(targetId);
        if (targetElement) {
            targetElement.scrollIntoView({
                behavior: 'smooth'
            });
        }
        setIsMobileMenuOpen(false); // Close mobile menu on click
    };

    return (
        <header className="fixed top-0 left-0 w-full bg-[#0a0a0a]/80 backdrop-blur-md z-50 border-b border-gray-800 shadow-xl">
            <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
                <a href="#home" onClick={handleScrollTo} className="block h-12 w-12" aria-label="Home">
                    <img src="https://app-cdn.readytensor.ai/users/33aa0c22-90bf-4953-9d6f-2f44559b8ed7/uploads/avatar-cb4db292-7d40-4896-8aa0-d16d0c4ad9bb.jpeg"
                         alt="Craig Michael Lawrence Dsouza Logo"
                         className="h-full w-full rounded-full object-cover border-2 border-indigo-400/50 hover:border-indigo-400 transition-colors" />
                </a>

                {/* Desktop Nav */}
                <div className="hidden md:flex space-x-6">
                    {navItems.map(item => (
                        <a 
                            key={item.label}
                            href={item.href} 
                            onClick={handleScrollTo}
                            className={`nav-link text-gray-300 hover:text-white ${activeSection === item.section ? 'active' : ''}`}
                        >
                            {item.label}
                        </a>
                    ))}
                </div>
                
                {/* Mobile Nav Button */}
                <button 
                    id="mobile-menu-button" 
                    className="md:hidden p-2 rounded-md text-gray-300 hover:text-white hover:bg-gray-800 focus:outline-none focus:bg-gray-800" 
                    aria-label="Toggle menu"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
                        <line x1="3" y1="12" x2="21" y2="12"></line>
                        <line x1="3" y1="6" x2="21" y2="6"></line>
                        <line x1="3" y1="18" x2="21" y2="18"></line>
                    </svg>
                </button>
            </nav>

            {/* Mobile Nav Menu */}
            <div id="mobile-menu" className={`md:hidden ${isMobileMenuOpen ? '' : 'hidden'} absolute top-full left-0 w-full bg-[#0a0a0a] border-b border-t border-gray-800 shadow-lg`}>
                {navItems.map(item => (
                    <a 
                        key={item.label}
                        href={item.href} 
                        onClick={handleScrollTo}
                        className={`block text-center px-4 py-3 hover:bg-gray-800 ${activeSection === item.section ? 'text-indigo-400' : 'text-gray-300 hover:text-white'}`}
                    >
                        {item.label}
                    </a>
                ))}
            </div>
        </header>
    );
};

// --- HERO SECTION ---
const Hero = () => {
    const handleScrollTo = (e) => {
        e.preventDefault();
        const targetId = e.currentTarget.getAttribute('href');
        const targetElement = document.querySelector(targetId);
        if (targetElement) {
            targetElement.scrollIntoView({
                behavior: 'smooth'
            });
        }
    };
    
    return (
        <section id="home" className="h-screen relative flex items-center justify-center text-center overflow-hidden">
            <ThreeJSBackground />
            
            {/* Hero Content */}
            <div className="z-10 relative p-6 max-w-3xl">
                <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold text-white mb-4 tracking-tighter">
                    CRAIG MICHAEL LAWRENCE DSOUZA
                </h1>
                <p className="text-base sm:text-lg md:text-2xl text-indigo-300 mb-8 font-light">
                    Expert in <span className="font-medium text-white">AI/ML</span>, 
                    <span className="font-medium text-white">Full-Stack Development</span>, and 
                    <span className="font-medium text-white">Game Design</span>,
                    turning complex challenges into scalable, high-impact solutions.
                </p>
                <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                    <a href="#projects" onClick={handleScrollTo} className="cta-glow bg-indigo-600 text-white font-semibold px-8 py-3 rounded-md hover:bg-indigo-500 transition-all duration-300 transform hover:scale-105 shadow-lg">
                        View My Projects
                    </a>
                    <a href="#contact" onClick={handleScrollTo} className="bg-gray-700 text-gray-100 font-semibold px-8 py-3 rounded-md hover:bg-gray-600 transition-all duration-300 transform hover:scale-105 shadow-lg">
                        Get In Touch
                    </a>
                </div>
            </div>
            
            {/* Scroll Down Indicator */}
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10">
                <a href="#about" onClick={handleScrollTo} aria-label="Scroll down">
                    <svg className="w-6 h-6 text-gray-400 animate-bounce" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3" />
                    </svg>
                </a>
            </div>
        </section>
    );
};

// --- ABOUT SECTION ---
const About = () => (
    <section id="about" className="py-24 bg-gray-900/50">
        <div className="container mx-auto px-6 max-w-6xl">
            <h2 className="reveal text-3xl sm:text-4xl font-bold text-center mb-12 text-white">
                <span className="text-indigo-300">About Me</span>
            </h2>
            
            <p className="reveal text-center text-lg sm:text-xl text-gray-300 mb-20 max-w-3xl mx-auto">
                I am committed to leading high-impact projects, fostering innovation, and driving cross-functional collaboration to develop scalable solutions for real-world challenges.
            </p>

            {/* Education */}
            <div className="reveal mb-20">
                <h3 className="text-2xl sm:text-3xl font-semibold mb-8 text-white text-center">Education</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Card 1: B.E. */}
                    <div className="interactive-card bg-gray-800 p-6 rounded-lg shadow-xl text-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 text-indigo-400 mx-auto mb-4"><path d="M22 10v6M2 10l10-5 10 5-10 5z"></path><path d="M6 12v5c0 5 6 5 6 5s6 0 6-5v-5"></path></svg>
                        <h4 className="text-lg sm:text-xl font-bold text-indigo-300">Bachelor of Engineering</h4>
                        <p className="text-base sm:text-lg text-gray-200">Computer Science & Eng.</p>
                        <p className="text-md text-gray-400 mb-2">St. Francis Institute of Technology</p>
                        <p className="text-sm font-mono text-gray-500">2022 - 2026</p>
                    </div>
                    {/* Card 2: H.S.C. */}
                    <div className="interactive-card bg-gray-800 p-6 rounded-lg shadow-xl text-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 text-indigo-400 mx-auto mb-4"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>
                        <h4 className="text-lg sm:text-xl font-bold text-indigo-300">H.S.C (P.C.M.B & I.T)</h4>
                        <p className="text-base sm:text-lg text-gray-200 mt-8">St. Xavier's College, Mumbai</p>
                    </div>
                    {/* Card 3: S.S.C. */}
                    <div className="interactive-card bg-gray-800 p-6 rounded-lg shadow-xl text-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 text-indigo-400 mx-auto mb-4"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>
                        <h4 className="text-lg sm:text-xl font-bold text-indigo-300">S.S.C</h4>
                        <p className="text-base sm:text-lg text-gray-200 mt-14">St. John XXIII High School</p>
                    </div>
                </div>
            </div>
            
            {/* Awards, Pubs, Certs */}
            <div className="reveal">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
                    {/* Card 1: Awards & Pubs */}
                    <div className="interactive-card bg-gray-800 p-6 rounded-lg shadow-xl">
                        <h3 className="text-2xl sm:text-3xl font-semibold mb-6 text-white">Awards & Publications</h3>
                        <div className="space-y-4">
                            <div>
                                <h4 className="text-lg sm:text-xl font-semibold text-indigo-300 mb-3">Awards</h4>
                                <ul className="space-y-2">
                                    <li className="p-3 rounded-md transition-colors hover:bg-gray-700/50 flex items-center text-gray-300"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-3 text-indigo-400 flex-shrink-0"><path d="M12 8V6m0 10v-2m0-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path><path d="M7.86 16.14l-1.99-2a4 4 0 00-3.12-3.12l-2-1.99A11.95 11.95 0 0112 2a11.95 11.95 0 0111.25 7.03l-2 1.99a4 4 0 00-3.12 3.12l-1.99 2A11.95 11.95 0 0112 22a11.95 11.95 0 01-4.14-5.86z"></path></svg>1st Prize – Smart India Hackathon 2023</li>
                                    <li className="p-3 rounded-md transition-colors hover:bg-gray-700/50 flex items-center text-gray-300"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-3 text-indigo-400 flex-shrink-0"><path d="M12 8V6m0 10v-2m0-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path><path d="M7.86 16.14l-1.99-2a4 4 0 00-3.12-3.12l-2-1.99A11.95 11.95 0 0112 2a11.95 11.95 0 0111.25 7.03l-2 1.99a4 4 0 00-3.12 3.12l-1.99 2A11.95 11.95 0 0112 22a11.95 11.95 0 01-4.14-5.86z"></path></svg>1st Prize – Prakalp 2024</li>
                                    <li className="p-3 rounded-md transition-colors hover:bg-gray-700/50 flex items-center text-gray-300"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-3 text-indigo-400 flex-shrink-0"><path d="M12 8V6m0 10v-2m0-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path><path d="M7.86 16.14l-1.99-2a4 4 0 00-3.12-3.12l-2-1.99A11.95 11.95 0 0112 2a11.95 11.95 0 0111.25 7.03l-2 1.99a4 4 0 00-3.12 3.12l-1.99 2A11.95 11.95 0 0112 22a11.95 11.95 0 01-4.14-5.86z"></path></svg>1st Prize – Prakalp 2023</li>
                                    <li className="p-3 rounded-md transition-colors hover:bg-gray-700/50 flex items-center text-gray-300"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-3 text-indigo-400 flex-shrink-0"><path d="M12 8V6m0 10v-2m0-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path><path d="M7.86 16.14l-1.99-2a4 4 0 00-3.12-3.12l-2-1.99A11.95 11.95 0 0112 2a11.95 11.95 0 0111.25 7.03l-2 1.99a4 4 0 00-3.12 3.12l-1.99 2A11.95 11.95 0 0112 22a11.95 11.95 0 01-4.14-5.86z"></path></svg>1st Prize – Colloquium 2024</li>
                                </ul>
                            </div>
                            <hr className="border-gray-700" />
                            <div>
                                <h4 className="text-lg sm:text-xl font-semibold text-indigo-300 mb-3">Publications</h4>
                                <ul className="space-y-2">
                                    <li className="p-3 rounded-md transition-colors hover:bg-gray-700/50 flex items-center text-gray-300"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-3 text-indigo-400 flex-shrink-0"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>ReadyTensor - GIL</li>
                                    <li className="p-3 rounded-md transition-colors hover:bg-gray-700/50 flex items-center text-gray-300"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-3 text-indigo-400 flex-shrink-0"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>IEEE - TIS framework</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    {/* Card 2: Certs */}
                    <div className="interactive-card bg-gray-800 p-6 rounded-lg shadow-xl">
                        <h3 className="text-2xl sm:text-3xl font-semibold mb-6 text-white">Certifications</h3>
                        <ul className="space-y-3">
                            <li className="p-4 rounded-md transition-colors hover:bg-gray-700/50 text-gray-300 text-base sm:text-lg flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-3 text-indigo-400 flex-shrink-0"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="m10 13-2 2 2 2"></path><path d="m14 13 2 2-2 2"></path></svg>
                                Cloud Computing - Linux Foundation
                            </li>
                            <li className="p-4 rounded-md transition-colors hover:bg-gray-700/50 text-gray-300 text-base sm:text-lg flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-3 text-indigo-400 flex-shrink-0"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="m10 13-2 2 2 2"></path><path d="m14 13 2 2-2 2"></path></svg>
                                Machine Learning with AI - Internshala
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

// --- VALUE PROPOSITION SECTION ---
const ValueProposition = () => (
    <section id="value-prop" className="py-24 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 max-w-7xl">
            <h2 className="reveal text-3xl sm:text-4xl font-bold text-center mb-16 text-white">
                <span className="text-indigo-300">My Value Proposition</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                
                {/* Value 1: Problem Solving */}
                <div className="reveal interactive-card bg-gray-800 p-6 rounded-lg shadow-xl" style={{ transitionDelay: '100ms' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 text-indigo-400 mb-4"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path><circle cx="12" cy="13" r="3"></circle></svg>
                    <h4 className="text-xl sm:text-2xl font-bold text-white mb-3">Creative Problem Solving</h4>
                    <p className="text-gray-300">From AI-driven pothole detection (NeuroNav Pro) to NLP-powered prompt refinement (GIL), I excel at deconstructing complex challenges into actionable, innovative solutions that deliver real-world results.</p>
                </div>

                {/* Value 2: Project Delivery */}
                <div className="reveal interactive-card bg-gray-800 p-6 rounded-lg shadow-xl" style={{ transitionDelay: '200ms' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 text-indigo-400 mb-4"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path><polyline points="12 6 12 12 16 14"></polyline></svg>
                    <h4 className="text-xl sm:text-2xl font-bold text-white mb-3">Project Management & Delivery</h4>
                    <p className="text-gray-300">Successfully led and delivered multiple award-winning projects (SIH, Prakalp), managing technical execution and team collaboration to ensure on-time, high-quality outcomes under pressure.</p>
                </div>

                {/* Value 3: Rapid Learning */}
                <div className="reveal interactive-card bg-gray-800 p-6 rounded-lg shadow-xl" style={{ transitionDelay: '300ms' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 text-indigo-400 mb-4"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path></svg>
                    <h4 className="text-xl sm:text-2xl font-bold text-white mb-3">Adaptability & Rapid Learning</h4>
                    <p className="text-gray-300">Mastered and deployed Godot for a 3D FPS game (BulletStorm) in just two weeks, demonstrating a proven ability to quickly learn and apply new technologies to meet ambitious project goals.</p>
                </div>

            </div>
        </div>
    </section>
);

// --- SKILLS SECTION ---
const Skills = () => {
    const skills = {
        "AI / Machine Learning": [
            "Machine Learning", "MLOps", "Artificial Intelligence", 
            "Natural Language Processing (NLP)", "Neural Networks", 
            "Computer Vision", "LLM Evaluation"
        ],
        "Full-Stack Development": [
            "Python", "JavaScript", "React", "Next.js", "Node.js", 
            "Express.js", "HTML5", "CSS3"
        ],
        "Databases, Cloud & Tools": [
            "PostgreSQL", "SQL", "MongoDB", "Docker", 
            "Google Cloud Platform (GCP)", "GitHub", "APIs", 
            "Data Structures", "OOP"
        ]
    };

    return (
        <section id="skills" className="py-24 bg-gray-900/50">
            <div className="container mx-auto px-6 max-w-5xl">
                <h2 className="reveal text-3xl sm:text-4xl font-bold text-center mb-16 text-white">
                    <span className="text-indigo-300">Technical Skills</span>
                </h2>
                
                <div className="space-y-12">
                    {Object.entries(skills).map(([category, list]) => (
                        <div className="reveal" key={category}>
                            <h3 className="text-2xl font-semibold text-indigo-300 mb-6">{category}</h3>
                            <div className="flex flex-wrap gap-3">
                                {list.map(skill => (
                                    <span key={skill} className="skill-pill text-gray-200 font-medium px-4 py-2 rounded-full">
                                        {skill}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
        
// --- PROJECTS SECTION ---
const Projects = () => {
    const projects = [
        {
            title: "GIL (GPT-Interpreted Language)",
            description: "A Python app to refine LLM prompts using Gemini and spaCy. Streamlines prompt engineering for efficiency and effectiveness.",
            github: "https://github.com/CraigMLdsouza/GIL",
            delay: "100ms"
        },
        {
            title: "Synthetica - AI Text Detector",
            description: "AI text detection platform (10k+ monthly submissions). Optimized ML deployment (React, Flask, Firebase) for high uptime and user engagement.",
            github: "https://github.com/CraigMLdsouza",
            live: "https://synthetica-3bf2d.web.app/",
            delay: "200ms"
        },
        {
            title: "SFITAssets - Stock Management",
            description: "College-level stock management system for efficient allocation of physical resources on campus. Actively used by SFIT.",
            github: "https://github.com/CraigMLdsouza",
            live: "https://sfitassets.web.app/login",
            delay: "300ms"
        },
        {
            title: "NeuroNav Pro (Prakalp Winner)",
            description: "AI-powered self-driving module for pothole detection and avoidance using advanced computer vision, sensor fusion, and image processing.",
            delay: "100ms"
        },
        {
            title: "SIH 2023 Ayusharks (Winner)",
            description: "Developed a national-level SIH 2023 winning, startup-ready platform for the AYUSH ecosystem, focusing on alternative medicine and community outreach.",
            delay: "200ms"
        },
        {
            title: "Autonomous Self-Healing Concrete",
            description: "Colloquium Winner. Created an innovative system that detects and autonomously repairs concrete cracks using embedded hardware and sensors.",
            delay: "300ms"
        }
    ];

    const games = [
        {
            title: "BulletStorm",
            description: "3D FPS game developed in 2 weeks using Godot. Showcased at MOSAIC tech fest and played by 300+ participants.",
            delay: "100ms"
        },
        {
            title: "GHOST PROTOCOL",
            date: "Apr 2025",
            description: "2D collaborative puzzle game with role-based mechanics; presented at MOSAIC, engaging over 200 players in team-based problem-solving.",
            delay: "200ms"
        },
        {
            title: "Uni8 ↗",
            date: "Apr 2025",
            description: "Online cipher and logic-based multiplayer game; hosted during MOSAIC and played by over 100 participants in a timed challenge format.",
            delay: "300ms"
        }
    ];

    return (
        <section id="projects" className="py-24 bg-[#0a0a0a]">
            <div className="container mx-auto px-6 max-w-7xl">
                <h2 className="reveal text-3xl sm:text-4xl font-bold text-center mb-16 text-white">
                    <span className="text-indigo-300">Featured Projects</span>
                </h2>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {projects.map(p => (
                        <div key={p.title} className="reveal interactive-card bg-gray-800 rounded-lg shadow-xl overflow-hidden flex flex-col" style={{ transitionDelay: p.delay }}>
                            <div className="p-6 flex flex-col flex-grow">
                                <h3 className="text-xl sm:text-2xl font-bold text-indigo-300 mb-3">{p.title}</h3>
                                <p className="text-gray-300 mb-4 flex-grow">{p.description}</p>
                                <div className="mt-auto flex space-x-4">
                                    {p.github && (
                                        <a href={p.github} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-indigo-300 hover:text-indigo-200 transition-colors">
                                            View on GitHub
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 ml-2">
                                                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.91 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                                            </svg>
                                        </a>
                                    )}
                                    {p.live && (
                                        <a href={p.live} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-teal-300 hover:text-teal-200 transition-colors">
                                            Live Demo
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 ml-2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Game Development */}
                <div className="mt-24">
                    <h2 className="reveal text-3xl sm:text-4xl font-bold text-center mb-6 text-white">
                        <span className="text-indigo-300">Game Development</span>
                    </h2>
                    <p className="reveal text-center text-lg sm:text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
                        Engaging 1000+ players with immersive experiences.
                    </p>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {games.map(g => (
                            <div key={g.title} className="reveal interactive-card bg-gray-800 rounded-lg shadow-xl overflow-hidden flex flex-col" style={{ transitionDelay: g.delay }}>
                                <div className="p-6 flex flex-col flex-grow">
                                    <h3 className="text-xl sm:text-2xl font-bold text-indigo-300 mb-2">{g.title}</h3>
                                    {g.date && <span className="text-sm font-mono text-gray-400 block mb-3">{g.date}</span>}
                                    <p className={`text-gray-300 mb-4 flex-grow ${!g.date ? 'mt-1' : ''}`}>{g.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// --- EXPERIENCE SECTION ---
const Experience = () => (
    <section id="experience" className="py-24 bg-gray-900/50">
        <div className="container mx-auto px-6">
            <h2 className="reveal text-3xl sm:text-4xl font-bold text-center mb-16 text-white">
                <span className="text-indigo-300">Work Experience</span>
            </h2>

            <div className="timeline-container">
                {/* Experience 1 */}
                <div className="timeline-item timeline-left">
                    <div className="timeline-content reveal">
                        <h3 className="text-lg sm:text-xl font-bold text-indigo-300">Software Development Internship</h3>
                        <h4 className="text-base sm:text-lg font-semibold text-gray-200 mb-2">Western Railways, Mumbai</h4>
                        <span className="text-sm font-mono text-gray-400 block mb-3">May 2025 - Jun 2025</span>
                        <ul className="list-disc list-inside text-gray-300 space-y-1">
                            <li>Built & deployed projects on Agentic AI and RAG models.</li>
                            <li>Developed data retrieval & visualization software using Python and Streamlit.</li>
                        </ul>
                    </div>
                </div>
                {/* Experience 2 */}
                <div className="timeline-item timeline-right">
                    <div className="timeline-content reveal">
                        <h3 className="text-lg sm:text-xl font-bold text-indigo-300">Web Developer Internship</h3>
                        <h4 className="text-base sm:text-lg font-semibold text-gray-200 mb-2">Nashabandimandal, Maharashtra</h4>
                        <span className="text-sm font-mono text-gray-400 block mb-3">Jan 2024 - Jul 2024</span>
                        <ul className="list-disc list-inside text-gray-300 space-y-1">
                            <li>Improved responsive design, boosting traffic by 30%.</li>
                            <li>Implemented mobile-first principles, growing mobile traffic by 15%.</li>
                            <li>Integrated 3rd-party APIs, enabling 20% faster load times.</li>
                        </ul>
                        <a href="https://nashabandimandalmaharashtraraj.web.app/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-teal-300 hover:text-teal-200 transition-colors mt-4">
                            Live Website
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 ml-2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                        </a>
                    </div>
                </div>
                {/* Experience 3 */}
                <div className="timeline-item timeline-left">
                    <div className="timeline-content reveal">
                        <h3 className="text-lg sm:text-xl font-bold text-indigo-300">Lead Software Developer Internship</h3>
                        <h4 className="text-base sm:text-lg font-semibold text-gray-200 mb-2">Aakriti Exports, Mumbai</h4>
                        <span className="text-sm font-mono text-gray-400 block mb-3">Sep 2023 - Jan 2024</span>
                        <ul className="list-disc list-inside text-gray-300 space-y-1">
                            <li>Enhanced multilingual functionality, increasing engagement by 30%.</li>
                            <li>Improved accessibility, boosting traffic by 18%.</li>
                            <li>Streamlined content management with APIs, reducing update time by 15%.</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

// --- EXTRACURRICULARS SECTION ---
const Extracurriculars = () => {
    const activities = [
        {
            title: "Technical Secretary, SFIT",
            description: "Organized and led major technical events, including MOSAIC and the Innov8 hackathon, demonstrating leadership and event management.",
            delay: "100ms"
        },
        {
            title: "Mixed Martial Arts Practitioner",
            description: "Actively trained in MMA, developing discipline, physical endurance, and mental resilience through regular training and sparring.",
            delay: "200ms"
        },
        {
            title: "Creative Writer",
            description: "Authored original poems and short stories exploring themes of identity, emotion, and modern life, showcasing strong written communication.",
            delay: "300ms"
        },
        {
            title: "Debater – Ethics & Technology",
            description: "Participated in college-level debates on topics like germline editing and Universal Basic Income, demonstrating critical thinking.",
            delay: "100ms"
        },
        {
            title: "Game Developer – Vedic Puzzle",
            description: "Designed a cooperative multiplayer puzzle game using React and Firebase, integrating roles within an interactive narrative.",
            delay: "200ms"
        },
        {
            title: "Event Organizer – MOSAIC & Innov8",
            description: "Handled logistics, coordination, and technical planning for large-scale college events, fostering teamwork and organizational effectiveness.",
            delay: "300ms"
        }
    ];

    return (
        <section id="extracurriculars" className="py-24 bg-[#0a0a0a]">
            <div className="container mx-auto px-6 max-w-7xl">
                <h2 className="reveal text-3xl sm:text-4xl font-bold text-center mb-16 text-white">
                    <span className="text-indigo-300">Extracurricular Activities</span>
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {activities.map(act => (
                        <div key={act.title} className="reveal interactive-card bg-gray-800 p-6 rounded-lg shadow-xl" style={{ transitionDelay: act.delay }}>
                            <h4 className="text-lg sm:text-xl font-bold text-indigo-300 mb-2">{act.title}</h4>
                            <p className="text-gray-300">{act.description}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// --- CONTACT SECTION ---
const Contact = () => (
    <section id="contact" className="py-24 bg-gray-900/50">
        <div className="container mx-auto px-6 max-w-3xl text-center">
            <h2 className="reveal text-3xl sm:text-4xl font-bold mb-6 text-white">
                <span className="text-indigo-300">Get In Touch</span>
            </h2>
            <p className="reveal text-lg sm:text-xl text-gray-300 mb-12">
                I'm always open to discussing new projects, creative ideas, or opportunities to be part of your vision.
            </p>

            <div className="reveal flex flex-col md:flex-row justify-center items-center gap-8 [perspective:1000px]">
                {/* Email */}
                <a href="mailto:dsouzacraigmichael@gmail.com" className="flex items-center justify-center space-x-3 p-4 bg-gray-800 rounded-lg border border-gray-700 hover:bg-gray-700 hover:border-indigo-500 transition-all duration-300 w-full md:w-auto [transform-style:preserve-3d] hover:[transform:rotateX(10deg)_rotateY(-5deg)_scale(1.05)]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-indigo-400 flex-shrink-0"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                    <span className="text-base sm:text-lg text-gray-200 break-all">dsouzacraigmichael@gmail.com</span>
                </a>
                
                {/* Phone */}
                <a href="tel:+917249478960" className="flex items-center justify-center space-x-3 p-4 bg-gray-800 rounded-lg border border-gray-700 hover:bg-gray-700 hover:border-indigo-500 transition-all duration-300 w-full md:w-auto [transform-style:preserve-3d] hover:[transform:rotateX(10deg)_rotateY(5deg)_scale(1.05)]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-indigo-400 flex-shrink-0"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                    <span className="text-base sm:text-lg text-gray-200">+91 7249478960</span>
                </a>
            </div>

            {/* Social Icons */}
            <div className="reveal flex justify-center space-x-8 mt-12 [perspective:1000px]">
                <a href="https://www.linkedin.com/in/craig-ml-dsouza/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-indigo-400 transition-all duration-300 [transform-style:preserve-3d] hover:scale-110 hover:[transform:rotateY(20deg)_translateZ(10px)]" aria-label="LinkedIn">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
                </a>
                <a href="https://github.com/CraigMLdsouza" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-indigo-400 transition-all duration-300 [transform-style:preserve-3d] hover:scale-110 hover:[transform:rotateY(-20deg)_translateZ(10px)]" aria-label="GitHub">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.91 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.h372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                    </svg>
                </a>
            </div>
        </div>
    </section>
);

// --- FOOTER COMPONENT ---
const Footer = () => (
    <footer className="py-8 bg-[#0a0a0a] border-t border-gray-800">
        <div className="container mx-auto px-6 text-center text-gray-500">
            <p>&copy; 2025 Craig Michael Lawrence Dsouza. All rights reserved.</p>
        </div>
    </footer>
);


// --- MAIN APP COMPONENT ---
export default function App() {
    const [activeSection, setActiveSection] = useState('home');
    
    // Custom hooks to manage animations and nav state
    useScrollReveal();
    useActiveNav(setActiveSection);

    return (
        <div className="bg-[#0a0a0a] text-gray-200 antialiased">
            <PortfolioStyles />
            <Header activeSection={activeSection} />
            <main>
                <Hero />
                <About />
                <ValueProposition />
                <Skills />
                <Projects />
                <Experience />
                <Extracurriculars />
                <Contact />
            </main>
            <Footer />
        </div>
    );
}
